import React, { useState, useEffect } from 'react';
import sanzarPhoto from '../assets/Sanzar.jpg';
import mainulPhoto from '../assets/Mainul.jpg';

const AboutUs = () => {
  const [expandedCard, setExpandedCard] = useState(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (expandedCard !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [expandedCard]);

  const team = [
    {
      name: 'Sanzar Adnan',
      title: 'Co-Founder & CEO',
      bio: 'Serial entrepreneur with expertise in AI and financial technology. Passionate about democratizing investment insights.',
      detailedBio: 'With over 8 years of experience in fintech and AI, Sanzar has led multiple successful ventures in the financial technology space. His vision is to democratize institutional-grade investment tools for retail investors worldwide.',
      experience: '8+ years in Tech',
      education: 'MSc, Computer Science',
      skills: ['AI & Machine Learning', 'Financial Analysis', 'Product Strategy', 'Team Leadership'],
      image: sanzarPhoto,
      linkedin: 'https://www.linkedin.com/in/sanzar-adnan-alam/',
      twitter: 'https://x.com/sanz_adnan',
      email: 'connect@traderbro.ai',
    },
    {
      name: 'Mainul Alin',
      title: 'Co-Founder & CTO',
      bio: 'Expert in artificial intelligence and financial modeling with a track record of building scalable systems.',
      detailedBio: 'Mainul is the deep domain expert who has turned \'full-on tech.\' He spent a decade as a professional equity analyst, watching retail investors get burned by the information gap. He now combines his 16 years of market-following with a mastery of AI for finance, leading the build of the solution he\'s been obsessed with for his entire career.',
      experience: '10+ years in Finance & AI',
      education: null,
      skills: ['Financial Analysis', 'AI & Machine Learning', 'Quantitative Modeling', 'System Architecture'],
      image: mainulPhoto,
      linkedin: 'https://www.linkedin.com/in/hasanmainul/',
      twitter: 'https://x.com/psycolin',
      email: 'mainul@traderbro.ai',
    },
  ];

  const toggleCard = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  return (
    <section id="about" className="bg-gradient-to-b from-gray-50 to-white py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#222425] mb-6 tracking-tight">
            Meet the Founders
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Making institutional-grade stock analysis accessible to everyone through AI.
          </p>
        </div>

        {/* Team Grid - Interactive Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl mx-auto">
          {team.map((member, index) => {
            
            return (
              <div
                key={index}
                onClick={() => toggleCard(index)}
                className="group relative bg-white rounded-3xl border border-gray-100 hover:border-[#00DA76]/30 hover:shadow-xl p-10 cursor-pointer transition-all duration-300 hover:-translate-y-1"
              >
                {/* Photo */}
                <div className="relative w-32 h-32 mx-auto mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#00DA76]/20 to-emerald-400/20 rounded-full blur-xl opacity-40 group-hover:opacity-60 transition-opacity duration-300"></div>
                  <img
                    src={member.image}
                    alt={member.name}
                    className="relative w-full h-full rounded-full object-cover border-4 border-white shadow-md transition-transform duration-300"
                  />
                </div>

                {/* Info */}
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-[#222425] mb-2">
                    {member.name}
                  </h3>
                  <p className="text-base text-[#00DA76] font-semibold mb-4">
                    {member.title}
                  </p>

                  {/* Social Links */}
                  <div className="flex justify-center gap-4">
                    <a
                      href={member.linkedin}
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#00DA76] transition-all duration-300 hover:scale-110 cursor-pointer"
                      title="LinkedIn"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </a>
                    <a
                      href={member.twitter}
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#00DA76] transition-all duration-300 hover:scale-110 cursor-pointer"
                      title="X (Twitter)"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal Overlay - Expanded View */}
        {expandedCard !== null && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
            onClick={() => toggleCard(null)}
          >
            <div 
              className="relative bg-white rounded-2xl sm:rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => toggleCard(null)}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 w-9 h-9 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors group z-10 shadow-lg"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="p-6 sm:p-8 md:p-12">

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                  {/* Left: Photo and Basic Info */}
                  <div className="text-center md:text-left">
                    <div className="relative w-40 h-40 sm:w-48 sm:h-48 mx-auto md:mx-0 mb-4 sm:mb-6">
                      <div className="absolute inset-0 bg-gradient-to-br from-brand-cta to-emerald-400 rounded-full blur-xl opacity-40"></div>
                      <img
                        src={team[expandedCard].image}
                        alt={team[expandedCard].name}
                        className="relative w-full h-full rounded-full object-cover border-4 border-white shadow-xl"
                      />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-brand-dark mb-2">
                      {team[expandedCard].name}
                    </h3>
                    <p className="text-brand-cta font-semibold text-base sm:text-lg mb-4 sm:mb-6">
                      {team[expandedCard].title}
                    </p>

                    {/* Social Links */}
                    <div className="flex justify-center md:justify-start gap-3 mb-6 sm:mb-8">
                      <a
                        href={team[expandedCard].linkedin}
                        onClick={(e) => e.stopPropagation()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-cta hover:text-[#00D492] transition-all duration-300 hover:scale-125 cursor-pointer group"
                        title="LinkedIn"
                      >
                        <svg className="w-6 h-6 group-hover:w-7 group-hover:h-7 transition-all duration-300" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      </a>
                      <a
                        href={team[expandedCard].twitter}
                        onClick={(e) => e.stopPropagation()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-cta hover:text-[#00D492] transition-all duration-300 hover:scale-125 cursor-pointer group"
                        title="X (Twitter)"
                      >
                        <svg className="w-6 h-6 group-hover:w-7 group-hover:h-7 transition-all duration-300" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                    </div>

                    {/* Quick Stats */}
                    <div className="space-y-4 text-sm bg-gradient-to-br from-gray-50 to-white p-4 rounded-2xl border border-gray-100">
                      <div className="flex items-start gap-3 text-gray-700">
                        <svg className="w-5 h-5 text-brand-cta mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <p className="font-semibold text-brand-dark">Experience</p>
                          <p className="text-gray-600">{team[expandedCard].experience}</p>
                        </div>
                      </div>
                      {team[expandedCard].education && (
                        <div className="flex items-start gap-3 text-gray-700">
                          <svg className="w-5 h-5 text-brand-cta mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                          <div>
                            <p className="font-semibold text-brand-dark">Education</p>
                            <p className="text-gray-600">{team[expandedCard].education}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Detailed Bio */}
                  <div className="md:col-span-2">
                    <h4 className="text-xl sm:text-2xl font-bold text-brand-dark mb-4 sm:mb-6 flex items-center gap-3">
                      <span className="w-1.5 h-6 sm:h-8 bg-brand-cta rounded"></span>
                      About {team[expandedCard].name.split(' ')[0]}
                    </h4>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base md:text-lg mb-6 sm:mb-8">
                      {team[expandedCard].detailedBio}
                    </p>

                    <div className="bg-gradient-to-br from-brand-cta/5 to-emerald-400/5 rounded-2xl p-6 border border-brand-cta/20">
                      <h5 className="font-bold text-brand-dark mb-4 text-lg">Core Expertise</h5>
                      <div className="flex flex-wrap gap-3">
                        {team[expandedCard].skills.map((skill, i) => (
                          <span key={i} className="px-4 py-2 bg-white text-brand-dark text-sm font-medium rounded-full border border-gray-200 hover:border-brand-cta hover:shadow-md transition-all">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Company Info */}
        <div className="mt-8 sm:mt-10 md:mt-12 text-center px-2">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-brand-cta/5 to-emerald-400/5 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 border border-brand-cta/20">
            <h3 className="text-xl sm:text-2xl font-bold text-brand-dark mb-3 sm:mb-4">
              Why TraderBro?
            </h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed mb-4 sm:mb-6">
              Democratizing institutional-grade financial intelligence through proprietary AI technology.
            </p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-brand-cta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Founded 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-brand-cta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Location: Worldwide</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-brand-cta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>AI-Powered Platform</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AboutUs;
